import { Card } from "@/components/ui/card";
import { Trophy, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { ProductivityMetrics } from "@/types/analytics";
import { motion } from "framer-motion";

interface ProductivityScoreProps {
  data: ProductivityMetrics;
}

export const ProductivityScore = ({ data }: ProductivityScoreProps) => {
  const getTrendIcon = (trend: 'up' | 'down' | 'stable') => {
    switch (trend) {
      case 'up':
        return <TrendingUp className="h-4 w-4 text-green-500" />;
      case 'down':
        return <TrendingDown className="h-4 w-4 text-red-500" />;
      default:
        return <Minus className="h-4 w-4 text-gray-500" />;
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-500';
    if (score >= 60) return 'text-blue-500';
    if (score >= 40) return 'text-yellow-500';
    return 'text-red-500';
  };

  const getImprovementText = (improvement: number, trend: 'up' | 'down' | 'stable') => {
    if (trend === 'stable') return 'Maintaining steady performance';
    return trend === 'up'
      ? `Improved by ${improvement.toFixed(1)}%`
      : `Decreased by ${improvement.toFixed(1)}%`;
  };

  const timeframes = [
    {
      title: "Today's Score",
      score: data.daily.score,
      details: [
        `${data.daily.tasksCompleted} tasks completed`,
        `Avg completion: ${data.daily.avgCompletionTime}`,
        `Urgent response: ${data.daily.urgentResponseTime}`
      ]
    },
    {
      title: "Weekly Performance",
      score: data.weekly.score,
      trend: data.weekly.trend,
      improvement: data.weekly.improvement,
      details: [getImprovementText(data.weekly.improvement, data.weekly.trend)]
    },
    {
      title: "Monthly Overview",
      score: data.monthly.score,
      trend: data.monthly.trend,
      improvement: data.monthly.improvement,
      details: [getImprovementText(data.monthly.improvement, data.monthly.trend)]
    }
  ];

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold">Productivity Score</h2>
        <Trophy className="h-5 w-5 text-yellow-500" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {timeframes.map((timeframe, index) => (
          <motion.div
            key={timeframe.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="p-4 border rounded-lg hover:shadow-md transition-shadow"
          >
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-medium">{timeframe.title}</h3>
              {'trend' in timeframe && timeframe.trend && getTrendIcon(timeframe.trend as 'up' | 'down' | 'stable')}
            </div>

            <div className="mb-3">
              <span className={`text-3xl font-bold ${getScoreColor(timeframe.score)}`}>
                {timeframe.score.toFixed(1)}
              </span>
              <span className="text-sm text-gray-500 ml-1">/ 100</span>
            </div>

            <div className="space-y-1">
              {timeframe.details.map((detail, i) => (
                <p key={i} className="text-sm text-gray-500">
                  {detail}
                </p>
              ))}
            </div>

            <div className="w-full bg-gray-100 rounded-full h-2 mt-3">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${timeframe.score}%` }}
                transition={{ duration: 1, delay: index * 0.1 }}
                className={`h-full rounded-full ${timeframe.score >= 80 ? 'bg-green-500' :
                    timeframe.score >= 60 ? 'bg-blue-500' :
                      timeframe.score >= 40 ? 'bg-yellow-500' :
                        'bg-red-500'
                  }`}
              />
            </div>
          </motion.div>
        ))}
      </div>
    </Card>
  );
};